import { useState, useEffect } from 'react';

/**
 * Hook to calculate and format elapsed time from a start timestamp.
 * Updates every second while active.
 *
 * @param startTime - The start timestamp in milliseconds (from Date.now()), or null if not active
 * @param isActive - Whether to actively calculate elapsed time
 * @returns Formatted elapsed time string (e.g., "15s", "1m 23s") or null if not active
 */
export function useElapsedTime(
  startTime: number | null,
  isActive: boolean
): string | null {
  const [elapsedTime, setElapsedTime] = useState<string | null>(null);

  useEffect(() => {
    if (!isActive || !startTime) {
      setElapsedTime(null);
      return;
    }

    const updateElapsedTime = () => {
      const elapsed = Date.now() - startTime;
      setElapsedTime(formatElapsedTime(elapsed));
    };

    // Update immediately
    updateElapsedTime();

    // Then update every second
    const interval = setInterval(updateElapsedTime, 1000);

    return () => clearInterval(interval);
  }, [startTime, isActive]);

  return elapsedTime;
}

/**
 * Format elapsed time in milliseconds to a human-readable string.
 *
 * Examples:
 * - 3000ms -> "3s"
 * - 65000ms -> "1m 5s"
 * - 125000ms -> "2m 5s"
 */
function formatElapsedTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);

  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}m ${remainingSeconds}s`;
}
